/**
 * Live connection health check — the source of truth behind the passive status
 * derived in {@link derivePassiveStatus}. Verifies a stored credential against
 * the platform *right now* so the user can tell a genuinely-dropped connection
 * from a healthy one.
 *
 * Only platforms whose credential is readable in the browser AND reachable from
 * it (directly, or via an existing /api/*-validate proxy) can be tested here:
 * Facebook (direct Graph call), TikTok and LINE (server proxies). Google's
 * credentials live in a service_role-only table / need the client_secret chain,
 * so they can't be verified from the browser yet — those fall back to passive
 * status. Keep this list in sync with the proxies under api/.
 *
 * @module services/accountHealth
 */

import { getSupabaseClient } from "@/config/supabase";
import { logError } from "@/services/logger";
import type { PlatformAccount, PlatformType } from "@/types";
import type { TFn } from "@/utils/channelCorrelation";

export type AccountHealth =
  | { state: "ready"; message: string }
  | { state: "failed"; message: string }
  | { state: "unsupported"; message: string };

const BROWSER_TESTABLE: ReadonlySet<PlatformType> = new Set<PlatformType>([
  "facebook",
  "tiktok",
  "line",
]);

/** Can this account be verified live? (drives the UI affordance) */
export function isHealthTestable(
  account: Pick<PlatformAccount, "platform" | "accountType" | "connectionMethod">,
): boolean {
  if (account.accountType === "demo_data") return false;
  if (BROWSER_TESTABLE.has(account.platform)) return true;
  // Google's credential is server-side only and is verified through
  // /api/account-health: Google Ads by service account, Analytics by exchanging
  // its stored refresh token. Google ADS on a plain token is still unsupported
  // there — the Ads API needs a developer token too, which no token connection
  // carries.
  if (account.platform === "google_analytics") return true;
  return account.platform === "google_ads" && account.connectionMethod === "service_account";
}

// All three validators share this shape — one mapper for all of them.
interface Validation {
  valid: boolean;
  accountName?: string;
  error?: string;
}

function toHealth(v: Validation, t: TFn): AccountHealth {
  if (v.valid) {
    return {
      state: "ready",
      message: v.accountName ? t("settings.healthReadyNamed", { account: v.accountName }) : t("settings.healthReady"),
    };
  }
  return { state: "failed", message: v.error ?? t("settings.healthNeedsReconnect") };
}

/**
 * EVERY health check runs server-side (/api/account-health).
 *
 * It began as the Google Service-Account path, whose private key lives in a
 * service_role-only table the browser cannot read. Facebook, TikTok and LINE
 * followed for the same reason in reverse: checking them from the browser
 * required the browser to hold a long-lived ad-platform token, so the token had
 * to be readable by every workspace member. The endpoint proves ownership
 * through the caller's own RLS before it reads any credential with
 * service_role — the caller's authority is unchanged, the credential simply
 * never travels to them.
 *
 * We send the caller's Supabase session token so the endpoint can enforce that.
 */
async function checkAccountHealthServerSide(account: PlatformAccount, t: TFn): Promise<AccountHealth> {
  const supabase = getSupabaseClient();
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) {
    return { state: "failed", message: t("settings.healthSignInAgain") };
  }

  const resp = await fetch("/api/account-health", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ account_id: account.id }),
  });
  const parsed = (await resp.json().catch(() => null)) as (Validation & { error?: string }) | null;
  if (parsed && typeof parsed.valid === "boolean") return toHealth(parsed, t);
  return { state: "failed", message: parsed?.error ?? t("settings.healthTestFailed") };
}

/**
 * Verify one account's stored credential against its platform.
 * Never throws — always resolves to an {@link AccountHealth} the UI can render.
 */
export async function checkAccountHealth(account: PlatformAccount, t: TFn): Promise<AccountHealth> {
  if (!isHealthTestable(account)) {
    return {
      state: "unsupported",
      message: t("settings.healthNoLiveTest"),
    };
  }

  try {
    // One path for every platform: the server holds the credentials.
    return await checkAccountHealthServerSide(account, t);
  } catch (err) {
    logError("AccountHealth", "Health check failed", err);
    return {
      state: "failed",
      message: err instanceof Error ? err.message : t("settings.healthTestFailed"),
    };
  }
}
